import React, { useCallback, useEffect, useState } from 'react';
import { IconCheck, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import {
  fetchThemes, createTheme, updateTheme, deleteTheme, activateTheme, deactivateTheme,
  DEFAULT_THEME,
} from '../../../../entities/theme';
import type { ThemeData, ThemeRecord } from '../../../../entities/theme';
import { Button } from '../../../../shared/ui/Button';
import { ThemeModal } from '../../../../features/admin-theme-form';

const SWATCH_KEYS: (keyof ThemeData)[] = ['--bg', '--surface', '--border', '--text', '--orange', '--blue'];

const ThemesSection: React.FC = () => {
  const [themes, setThemes] = useState<ThemeRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<{ mode: 'add' | 'edit'; theme?: ThemeRecord } | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    fetchThemes()
      .then(setThemes)
      .catch(() => setThemes([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data: ThemeData) => {
    if (modal?.mode === 'edit' && modal.theme) {
      await updateTheme(modal.theme.id, data);
    } else {
      await createTheme(data);
    }
    load();
  };

  const handleActivate = async (theme: ThemeRecord) => {
    setBusy(theme.id);
    try {
      theme.isCurrent ? await deactivateTheme(theme.id) : await activateTheme(theme.id);
      load();
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async (id: string) => {
    setBusy(id);
    try {
      await deleteTheme(id);
      setConfirmDeleteId(null);
      load();
    } finally {
      setBusy(null);
    }
  };

  const hasActive = themes.some(t => t.isCurrent);

  return (
    <div className="adm-section">
      <div className="adm-section-header adm-section-header-row">
        <div>
          <h2 className="adm-section-title">Темы оформления</h2>
          <p className="adm-section-sub">Настройте цветовую схему интерфейса</p>
        </div>
        <Button variant="adm-add" onClick={() => setModal({ mode: 'add' })}>
          <IconPlus size={15} stroke={2.5} /> Создать тему
        </Button>
      </div>

      {loading ? (
        <div className="adm-loading">Загрузка...</div>
      ) : (
        <div className="adm-ut-wrap">
          <table className="adm-ut">
            <thead>
              <tr>
                <th>Тема</th>
                <th>Цвета</th>
                <th>Статус</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <tr className="adm-ut-row">
                <td>
                  <span className="adm-ut-name">По умолчанию</span>
                  <span className="adm-ut-email">Системная, нередактируемая</span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {SWATCH_KEYS.map(k => (
                      <div key={k} title={`${k}: ${DEFAULT_THEME[k]}`} style={{ width: 20, height: 20, borderRadius: 4, background: DEFAULT_THEME[k], border: '1.5px solid var(--border)', flexShrink: 0 }} />
                    ))}
                  </div>
                </td>
                <td>
                  {!hasActive && <span className="adm-role-badge admin">Активна</span>}
                </td>
                <td />
              </tr>

              {themes.map(theme => (
                <tr key={theme.id} className="adm-ut-row">
                  <td>
                    <span className="adm-ut-name">{theme.data.name}</span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {SWATCH_KEYS.map(k => (
                        <div key={k} title={`${k}: ${theme.data[k]}`} style={{ width: 20, height: 20, borderRadius: 4, background: theme.data[k], border: '1.5px solid var(--border)', flexShrink: 0 }} />
                      ))}
                    </div>
                  </td>
                  <td>
                    {theme.isCurrent && <span className="adm-role-badge admin">Активна</span>}
                  </td>
                  <td className="adm-ut-actions-cell">
                    {confirmDeleteId === theme.id ? (
                      <div className="adm-ut-confirm">
                        <Button variant="adm-delete" onClick={() => handleDelete(theme.id)} disabled={busy === theme.id}>
                          Удалить
                        </Button>
                        <Button variant="adm-cancel" onClick={() => setConfirmDeleteId(null)}>
                          Отмена
                        </Button>
                      </div>
                    ) : (
                      <div className="adm-ut-actions">
                        <Button
                          variant="adm-edit"
                          title={theme.isCurrent ? 'Деактивировать' : 'Активировать'}
                          onClick={() => handleActivate(theme)}
                          disabled={busy === theme.id}
                        >
                          <IconCheck size={13} stroke={2.5} />
                        </Button>
                        <Button variant="adm-edit" title="Редактировать" onClick={() => setModal({ mode: 'edit', theme })}>
                          <IconPencil size={13} stroke={2} />
                        </Button>
                        <Button
                          variant="adm-delete"
                          title={theme.isCurrent ? 'Нельзя удалить активную тему' : 'Удалить'}
                          disabled={theme.isCurrent}
                          onClick={() => setConfirmDeleteId(theme.id)}
                        >
                          <IconTrash size={13} stroke={2} />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}

              {themes.length === 0 && (
                <tr>
                  <td colSpan={4} className="adm-empty">Пользовательских тем нет</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <ThemeModal
          initial={modal.theme?.data}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

export { ThemesSection };
